import React, { useState } from "react";
import PropertyImagePlaceholder from "../../../components/Common/PropertyImagePlaceholder";

// ── Styles ────────────────────────────────────────────────────────────────────
const s = {
  page: {
    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    color: "#1a1d23",
  },
  card: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    overflow: "hidden",
  },
  cardHead: {
    padding: "0.75rem 1.125rem",
    borderBottom: "1px solid #e5e7eb",
    background: "#fafafa",
    fontSize: 13,
    fontWeight: 600,
    color: "#1a1d23",
  },
  cardBody: { padding: "1rem 1.125rem" },
  sectionLabel: {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: "0.07em",
    textTransform: "uppercase",
    color: "#6b7280",
    marginBottom: 4,
  },
  fieldLabel: {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    color: "#6b7280",
    marginBottom: 2,
  },
  fieldValue: { fontSize: 13, color: "#1a1d23" },
  pill: (variant) => {
    const map = {
      blue: { background: "#EFF6FF", color: "#1E40AF" },
      teal: { background: "#F0FDFA", color: "#115E59" },
      green: { background: "#F0FDF4", color: "#166534" },
      amber: { background: "#FFFBEB", color: "#92400E" },
      red: { background: "#FEF2F2", color: "#991B1B" },
      purple: { background: "#F5F3FF", color: "#5B21B6" },
      gray: { background: "#F9FAFB", color: "#4B5563", border: "1px solid #e5e7eb" },
    };
    return {
      fontSize: 11,
      fontWeight: 600,
      padding: "2px 9px",
      borderRadius: 20,
      whiteSpace: "nowrap",
      display: "inline-block",
      ...(map[variant] || map.gray),
    };
  },
  btn: {
    fontSize: 11,
    fontWeight: 500,
    padding: "5px 12px",
    borderRadius: 6,
    border: "1px solid #d1d5db",
    background: "transparent",
    color: "#374151",
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  btnPrimary: {
    fontSize: 12,
    fontWeight: 600,
    padding: "6px 16px",
    borderRadius: 8,
    border: "none",
    background: "#1E40AF",
    color: "#fff",
    cursor: "pointer",
    whiteSpace: "nowrap",
    flexShrink: 0,
  },
};

// ── Helpers ───────────────────────────────────────────────────────────────────
const dash = (v) => (v != null && String(v).trim() !== "" ? v : "—");

const formatPrice = (price) =>
  new Intl.NumberFormat("en-TT", {
    style: "currency",
    currency: "TTD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(price);

const formatDate = (date) => {
  if (!date) return "—";
  return new Date(date).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const statusPillVariant = (status) => {
  switch (status) {
    case "available":
    case "active":
      return "green";
    case "rented":
    case "occupied":
    case "pending":
      return "amber";
    case "under_contract":
      return "purple";
    case "sold":
      return "red";
    default:
      return "gray";
  }
};

const typePillVariant = (type) => {
  switch (type) {
    case "one_time_rental":
      return "blue";
    case "property_management":
      return "teal";
    case "sale":
      return "red";
    case "multi_unit_building":
      return "purple";
    default:
      return "gray";
  }
};

const statusLabel = (status) =>
  status ? status.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "Unknown";

const typeLabel = (type) => {
  switch (type) {
    case "one_time_rental":
      return "One-time rental";
    case "property_management":
      return "Property management";
    case "sale":
      return "Sale";
    case "multi_unit_building":
      return "Multi-unit building";
    default:
      return type;
  }
};

// ── Sub-components ────────────────────────────────────────────────────────────
const Pill = ({ variant, children }) => <span style={s.pill(variant)}>{children}</span>;

const SectionCard = ({ title, children }) => (
  <div style={{ ...s.card, marginBottom: "1rem" }}>
    <div style={s.cardHead}>{title}</div>
    <div style={s.cardBody}>{children}</div>
  </div>
);

const FieldRow = ({ label, value }) => (
  <div>
    <div style={s.fieldLabel}>{label}</div>
    <div style={s.fieldValue}>{value}</div>
  </div>
);

const ChevronLeft = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const ChevronRight = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

// ── Main component ────────────────────────────────────────────────────────────
function PropertyOverview({ property, onConvertClick }) {
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  if (!property) {
    return (
      <div style={{ textAlign: "center", padding: "2rem", fontSize: 14, color: "#6b7280" }}>
        Property not found
      </div>
    );
  }

  const images = property.images || [];
  const hasImages = images.length > 0;

  const prevImage = () => setActiveImageIndex((i) => (i === 0 ? images.length - 1 : i - 1));
  const nextImage = () => setActiveImageIndex((i) => (i === images.length - 1 ? 0 : i + 1));

  // ── Landlord / owner / seller section config ───────────────────────────────
  const landlordSection = (() => {
    const { type } = property;
    if (type === "one_time_rental") {
      const li = property.placementInfo?.landlordInfo;
      return {
        title: "Landlord information",
        rows: [
          { label: "Name", value: dash(li?.landlordName) },
          { label: "Phone", value: dash(li?.landlordContact) },
        ],
      };
    }
    if (type === "property_management") {
      const oi = property.managementInfo?.ownerInfo;
      return {
        title: "Property owner",
        rows: [
          { label: "Name", value: dash(oi?.ownerName) },
          { label: "Phone", value: dash(oi?.ownerContact) },
          { label: "Email", value: dash(oi?.ownerEmail) },
        ],
      };
    }
    if (type === "sale") {
      const si = property.saleInfo?.sellerInfo;
      return {
        title: "Seller",
        rows: [
          { label: "Name", value: dash(si?.sellerName) },
          { label: "Phone", value: dash(si?.sellerContact) },
        ],
      };
    }
    if (type === "multi_unit_building") {
      const pm = property.propertyManager;
      return {
        title: "Building owner / manager",
        rows: [
          { label: "Name", value: dash(pm?.name) },
          { label: "Phone", value: dash(pm?.contact) },
          { label: "Email", value: dash(pm?.email) },
        ],
      };
    }
    return null;
  })();

  // ── Tenant info ───────────────────────────────────────────────────────────
  const showTenantCard =
    (property.status === "rented" || property.status === "occupied") &&
    property.placementInfo?.tenantPlaced;

  const tenantFromPlacement = property.placementInfo?.tenantInfo?.tenantId?.personalInfo;
  const tenantFromOccupancy = property.occupancy?.currentTenant?.personalInfo;
  const tenantInfo = tenantFromPlacement || tenantFromOccupancy;

  const leaseEndDate =
    property.placementInfo?.tenantInfo?.leaseEndDate || property.occupancy?.leaseEnd;

  // ── Price display ─────────────────────────────────────────────────────────
  const isRental = property.type === "one_time_rental" || property.type === "property_management";
  const isSale = property.type === "sale";
  const rent = property.pricing?.rent;
  const salePrice = property.pricing?.salePrice;

  // ── Property specs ────────────────────────────────────────────────────────
  const specs = [
    { label: "Bedrooms", value: dash(property.propertyDetails?.bedrooms) },
    { label: "Bathrooms", value: dash(property.propertyDetails?.bathrooms) },
    { label: "Sq ft", value: dash(property.propertyDetails?.squareFootage) },
    { label: "Type", value: dash(property.propertyDetails?.propertyType) },
    ...(property.propertyDetails?.yearBuilt
      ? [{ label: "Year built", value: property.propertyDetails.yearBuilt }]
      : []),
    ...(property.propertyDetails?.lotSize > 0
      ? [{ label: "Lot size", value: `${property.propertyDetails.lotSize.toLocaleString()} sqft` }]
      : []),
  ];

  return (
    <div style={s.page}>
      {/* ── Image carousel ── */}
      {hasImages && (
        <div style={{ ...s.card, marginBottom: "1.25rem", position: "relative" }}>
          <img
            src={images[activeImageIndex].url}
            alt={`Property view ${activeImageIndex + 1}`}
            style={{ width: "100%", height: 260, objectFit: "cover", display: "block" }}
          />
          {images.length > 1 && (
            <>
              <button
                onClick={prevImage}
                style={{
                  position: "absolute",
                  top: "50%",
                  left: 10,
                  transform: "translateY(-50%)",
                  background: "rgba(255,255,255,0.85)",
                  border: "1px solid #e5e7eb",
                  borderRadius: 6,
                  width: 32,
                  height: 32,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  color: "#374151",
                }}
              >
                <ChevronLeft />
              </button>
              <button
                onClick={nextImage}
                style={{
                  position: "absolute",
                  top: "50%",
                  right: 10,
                  transform: "translateY(-50%)",
                  background: "rgba(255,255,255,0.85)",
                  border: "1px solid #e5e7eb",
                  borderRadius: 6,
                  width: 32,
                  height: 32,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  color: "#374151",
                }}
              >
                <ChevronRight />
              </button>
              <div
                style={{
                  position: "absolute",
                  bottom: 10,
                  right: 12,
                  background: "rgba(0,0,0,0.5)",
                  color: "#fff",
                  fontSize: 11,
                  fontWeight: 500,
                  padding: "2px 8px",
                  borderRadius: 20,
                }}
              >
                {activeImageIndex + 1} / {images.length}
              </div>
              {/* Dot indicators */}
              <div
                style={{
                  position: "absolute",
                  bottom: 10,
                  left: "50%",
                  transform: "translateX(-50%)",
                  display: "flex",
                  gap: 5,
                }}
              >
                {images.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveImageIndex(i)}
                    style={{
                      width: i === activeImageIndex ? 18 : 6,
                      height: 6,
                      borderRadius: 3,
                      border: "none",
                      background: i === activeImageIndex ? "#fff" : "rgba(255,255,255,0.5)",
                      cursor: "pointer",
                      padding: 0,
                      transition: "width 0.2s ease",
                    }}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      )}
      {!hasImages && (
        <div style={{ ...s.card, marginBottom: "1.25rem", overflow: "hidden" }}>
          <PropertyImagePlaceholder height={260} label="No images available" />
        </div>
      )}

      {/* ── Two-column layout ── */}
      <div
        className="re-stack-sm"
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr) minmax(0, 320px)",
          gap: "1rem",
          alignItems: "start",
        }}
      >
        {/* ── Left column ── */}
        <div>
          {/* Price + status strip */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 10,
              marginBottom: "1rem",
            }}
          >
            <div>
              {isRental && rent && (
                <div>
                  <span style={{ fontSize: 24, fontWeight: 700, color: "#1E40AF" }}>
                    {formatPrice(rent)}
                  </span>
                  <span style={{ fontSize: 13, color: "#9ca3af", marginLeft: 4 }}>/month</span>
                </div>
              )}
              {isSale && salePrice && (
                <span style={{ fontSize: 24, fontWeight: 700, color: "#1E40AF" }}>
                  {formatPrice(salePrice)}
                </span>
              )}
            </div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              <Pill variant={statusPillVariant(property.status)}>
                {statusLabel(property.status)}
              </Pill>
              <Pill variant={typePillVariant(property.type)}>{typeLabel(property.type)}</Pill>
            </div>
          </div>

          {/* Address */}
          <SectionCard title="Address">
            <div style={{ fontSize: 13, lineHeight: 1.6 }}>
              {property.address?.street && <div>{property.address.street}</div>}
              <div>
                {[property.address?.city, property.address?.parish, property.address?.zipCode]
                  .filter(Boolean)
                  .join(", ")}
              </div>
            </div>
          </SectionCard>

          {/* Landlord / owner / seller */}
          {landlordSection && (
            <SectionCard title={landlordSection.title}>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                  gap: "0.75rem",
                }}
              >
                {landlordSection.rows.map((row) => (
                  <FieldRow key={row.label} label={row.label} value={row.value} />
                ))}
              </div>
            </SectionCard>
          )}

          {/* Description */}
          {property.description && (
            <SectionCard title="Description">
              <p style={{ fontSize: 13, lineHeight: 1.65, color: "#374151", margin: 0 }}>
                {property.description}
              </p>
            </SectionCard>
          )}

          {/* Notes */}
          {property.notes && (
            <SectionCard title="Notes">
              <p style={{ fontSize: 13, lineHeight: 1.65, color: "#374151", margin: 0 }}>
                {property.notes}
              </p>
            </SectionCard>
          )}

          {/* Tenant info */}
          {showTenantCard && (
            <div
              style={{
                ...s.card,
                marginBottom: "1rem",
                borderColor: "#FDE68A",
              }}
            >
              <div
                style={{
                  ...s.cardHead,
                  background: "#FFFBEB",
                  borderBottomColor: "#FDE68A",
                  color: "#92400E",
                }}
              >
                {property.type === "one_time_rental" ? "Last linked tenant" : "Current tenant"}
              </div>
              <div style={s.cardBody}>
                {tenantInfo ? (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                      gap: "0.75rem",
                    }}
                  >
                    <FieldRow
                      label="Name"
                      value={
                        `${tenantInfo.firstName || ""} ${tenantInfo.lastName || ""}`.trim() || "—"
                      }
                    />
                    <FieldRow label="Phone" value={dash(tenantInfo.phone)} />
                    {leaseEndDate && (
                      <FieldRow
                        label="Lease end"
                        value={
                          <span style={{ color: "#991B1B", fontWeight: 600 }}>
                            {formatDate(leaseEndDate)}
                          </span>
                        }
                      />
                    )}
                  </div>
                ) : (
                  <div style={{ fontSize: 13, color: "#9ca3af" }}>
                    Tenant information not available
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Location details */}
          {(property.location?.neighborhood || property.location?.schoolDistrict) && (
            <SectionCard title="Location details">
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                  gap: "0.75rem",
                }}
              >
                {property.location.neighborhood && (
                  <FieldRow label="Neighbourhood" value={property.location.neighborhood} />
                )}
                {property.location.schoolDistrict && (
                  <FieldRow label="School district" value={property.location.schoolDistrict} />
                )}
                {property.location.coordinates?.coordinates && (
                  <FieldRow
                    label="Coordinates"
                    value={`${property.location.coordinates.coordinates[1].toFixed(4)}, ${property.location.coordinates.coordinates[0].toFixed(4)}`}
                  />
                )}
              </div>
            </SectionCard>
          )}

          {/* Convert to management CTA */}
          {property.type === "one_time_rental" &&
            property.status === "rented" &&
            onConvertClick &&
            !property.isDeleted && (
              <div
                style={{
                  ...s.card,
                  marginBottom: "1rem",
                  borderColor: "#BFDBFE",
                }}
              >
                <div
                  style={{
                    padding: "1rem 1.125rem",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 16,
                    flexWrap: "wrap",
                  }}
                >
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 3 }}>
                      Convert to property management
                    </div>
                    <div style={{ fontSize: 12, color: "#6b7280", lineHeight: 1.5 }}>
                      The landlord wants ongoing management. Convert this property to track monthly
                      management fees going forward.
                    </div>
                  </div>
                  <button style={s.btnPrimary} onClick={onConvertClick}>
                    Convert
                  </button>
                </div>
              </div>
            )}
        </div>

        {/* ── Right column ── */}
        <div>
          {/* Property specs */}
          <SectionCard title="Property details">
            <div
              className="re-stack-sm"
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "1rem",
              }}
            >
              {specs.map(({ label, value }) => (
                <div key={label} style={{ textAlign: "center" }}>
                  <div style={{ fontSize: 20, fontWeight: 700, color: "#1E40AF" }}>{value}</div>
                  <div style={{ ...s.sectionLabel, fontSize: 10, marginTop: 3 }}>{label}</div>
                </div>
              ))}
            </div>
          </SectionCard>

          {/* Amenities */}
          {property.amenities?.length > 0 && (
            <SectionCard title="Amenities">
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {property.amenities.map((amenity, i) => (
                  <Pill key={i} variant="gray">
                    {amenity.replace(/_/g, " ")}
                  </Pill>
                ))}
              </div>
            </SectionCard>
          )}
        </div>
      </div>

      {/* ── Metadata footer ── */}
      <div
        style={{
          marginTop: "1.5rem",
          paddingTop: "0.875rem",
          borderTop: "1px solid #e5e7eb",
          display: "flex",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 6,
        }}
      >
        <div style={{ fontSize: 12, color: "#9ca3af" }}>
          <span style={{ fontWeight: 500, color: "#6b7280" }}>Created</span>{" "}
          {formatDate(property.createdAt)}
        </div>
        <div style={{ fontSize: 12, color: "#9ca3af" }}>
          <span style={{ fontWeight: 500, color: "#6b7280" }}>Last updated</span>{" "}
          {formatDate(property.updatedAt)}
        </div>
      </div>
    </div>
  );
}

export default PropertyOverview;
